import { useWallet } from "@manahippo/aptos-wallet-adapter";
import React, { useEffect, useState, useMemo } from "react";
// import { useDispatch } from "react-redux";

import Button from "../../components/Common/Button";
import Card from "../../components/Common/Card";
import SettingsIcon from "../../components/Common/SettingsIcon";
import TransactionDialog from "../../components/Common/TransactionDialog";
import Divider from "../../components/Divider";
import Impact from "../../components/Impact";
import LoginWallet from "../../components/LoginWallet";
import PriceInfo from "../../components/Swap/PriceInfo";
import SwapInCard from "../../components/Swap/SwapInCard";
import SwapOutCard from "../../components/Swap/SwapOutCard";
import SwapIcon from "../../imgs/swap.svg";
import { getTransactionByHash } from "../../libs/aptos_sdk";
import coins from "../../libs/coins/coins.json";
import {
  Pool,
  TradeRoute,
  Trade,
  getTradeRoute,
  getTradeRouteOut,
} from "../../libs/pool";
import {
  getExpectedOut,
  getExpectedIn,
  get_all_pools,
  getDecimalsFromTradeRoute,
  getSymbolsFromTradeRoute,
  swapExactInput,
  swapExactOutput,
} from "../../libs/swap_sdk";
// import { end_loading, start_loading } from "../../store/loading";
import { sleep } from "../../utils/common";

export default function Swap() {
  // const dispatch = useDispatch();
  const { signAndSubmitTransaction, account } = useWallet();

  const [inCoin, setInCoin] = useState<string>(coins[0].address);
  const [outCoin, setOutCoin] = useState<string>(coins[1].address);
  const [inAmount, setInAmount] = useState("0");
  // const [feeRate, setFeeRate] = useState(0);
  const [outAmount, setOutAmount] = useState("0");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);
  const [txn, setTxn] = useState<string>("");
  // const [pool, setPool] = useState<Pool | undefined>(undefined);
  const [tradeRoute, setTradeRoute] = useState<TradeRoute | null>(null);
  const [swapBtnDisable, setBtnSwapDisable] = useState<boolean>(true);
  const [swapBtnText, setSwapBtnText] = useState<string>("Enter an amount");
  const [slippageRate, setSlippageRate] = useState<string>("0.1");
  const [isExactInAmount, setIsExactInAmount] = useState<boolean>(true);
  const [inSymbol, outSymbol] = useMemo(() => {
    if (!tradeRoute) {
      return ["", ""];
    }
    const symbols = getSymbolsFromTradeRoute(tradeRoute);
    if (!symbols) return ["", ""];
    return symbols;
  }, [tradeRoute]);

  const inRef = React.createRef();
  const outRef = React.createRef();
  const impactRef = React.createRef();

  const [allPairs, setAllPairs] = useState<Pool[]>([]);

  useEffect(() => {
    get_all_pools().then((all_pairs) => {
      setAllPairs(all_pairs);
    });
  }, []);

  useEffect(() => {
    if (inRef && inRef.current && outRef && outRef.current) {
      // @ts-ignore
      const balanceIn = inRef.current.balance;
      if (Number(inAmount) > Number(balanceIn)) {
        setBtnSwapDisable(true);
        setSwapBtnText("Insufficient in coin balance");
      } else if (Number(inAmount) > 0) {
        setBtnSwapDisable(false);
        setSwapBtnText("Swap");
      } else {
        setBtnSwapDisable(true);
        setSwapBtnText("Enter an amount");
      }
    }
  }, [inAmount, inRef, outAmount, outRef]);

  React.useEffect(() => {
    if (!(Number(inAmount) > 0 || Number(outAmount) > 0)) {
      return;
    }
    if (inCoin && outCoin) {
      const trade: Trade = {
        in_coin: inCoin,
        out_coin: outCoin,
        in_amount: Number(inAmount),
        out_amount: Number(outAmount),
      };
      if (Number(inAmount) > 0) {
        const data = getTradeRoute(trade, allPairs);
        setTradeRoute(data);
      } else if (Number(outAmount) > 0) {
        const data = getTradeRouteOut(trade, allPairs);
        setTradeRoute(data);
      }
    }
  }, [inCoin, outCoin, allPairs]);

  const handle_swap = async () => {
    setTxn("");
    setOpenAlert(true);
    setStep(0);

    if (!tradeRoute) return;

    const decimalsFromRoute = getDecimalsFromTradeRoute(tradeRoute);
    if (!decimalsFromRoute) {
      return;
    }
    const [in_decimals, out_decimals] = decimalsFromRoute;

    let data;
    if (isExactInAmount) {
      const inAmount1 = parseInt(
        (Number(inAmount) * Math.pow(10, in_decimals)).toFixed(0),
        10
      );
      let minOutAmount1 = parseInt(
        (Number(outAmount) * Math.pow(10, out_decimals)).toFixed(0),
        10
      );
      minOutAmount1 = parseInt(
        (minOutAmount1 * (1 - Number(slippageRate) / 100)).toFixed(),
        10
      );
      data = await swapExactInput(
        signAndSubmitTransaction,
        tradeRoute,
        inAmount1,
        minOutAmount1
      );
    } else {
      let maxInAmount1 = parseInt(
        (Number(inAmount) * Math.pow(10, in_decimals)).toFixed(0),
        10
      );
      const outAmount1 = parseInt(
        (Number(outAmount) * Math.pow(10, out_decimals)).toFixed(0),
        10
      );

      maxInAmount1 = parseInt(
        (maxInAmount1 * (1 + Number(slippageRate) / 100)).toFixed(),
        10
      );
      data = await swapExactOutput(
        signAndSubmitTransaction,
        tradeRoute,
        outAmount1,
        maxInAmount1
      );
    }

    setTxn(data.hash);

    setStep(1);
    let transaction;
    while (!transaction) {
      try {
        transaction = await getTransactionByHash(data.hash);
      } catch {
        await sleep(500);
      }
    }
    setStep(2);
  };

  const handle_in_amount = async (v: any) => {
    setInAmount(v);
    setIsExactInAmount(true);
    if (!tradeRoute) {
      const trade: Trade = {
        in_coin: inCoin,
        out_coin: outCoin,
        in_amount: Number(v),
        out_amount: Number(outAmount),
      };
      const data = getTradeRoute(trade, allPairs);
      if (data) {
        const expectedOut = getExpectedOut(data);
        (outRef.current as any).setAmount(expectedOut);
        setOutAmount(expectedOut);
      }
      setTradeRoute(data);
    } else {
      tradeRoute.trade.in_amount = v;
      const expectedOut = getExpectedOut(tradeRoute);
      (outRef.current as any).setAmount(expectedOut);
      setOutAmount(expectedOut);
    }
  };

  const handle_out_amount = async (v: any) => {
    setOutAmount(v);
    setIsExactInAmount(false);
    if (!tradeRoute) {
      const trade: Trade = {
        in_coin: inCoin,
        out_coin: outCoin,
        in_amount: Number(v),
        out_amount: Number(outAmount),
      };
      const data = getTradeRouteOut(trade, allPairs);
      if (data) {
        const expectedOut = getExpectedOut(data);
        (outRef.current as any).setAmount(expectedOut);
        setOutAmount(expectedOut);
      }
      setTradeRoute(data);
    } else {
      tradeRoute.trade.out_amount = v;
      const expectedIn = getExpectedIn(tradeRoute);
      (inRef.current as any).setAmount(expectedIn);
      setInAmount(expectedIn);
    }
  };

  const handleCloseAlert = () => setOpenAlert(false);

  const onSwitchPairsClick = () => {
    const tempAddress = inCoin;
    setInCoin(outCoin);
    setOutCoin(tempAddress);
  };

  return (
    <div className="overflow-auto no-scrollbar flex justify-center mt-36 mb-10">
      <div className="flex flex-col w-1/3 max-xl:w-3/4 max-sm:w-full max-sm:px-2">
        <Card>
          <div className="flex justify-between items-center py-4 px-4">
            <div className="text-white text-xl font-normal">SWAP</div>
            <SettingsIcon
              onClick={() => {
                const current: any = impactRef.current;
                current.open();
              }}
            />
          </div>
          <div className="p-2 content-center">
            <div className="w-full">
              <SwapInCard
                inCoin={inCoin}
                changeCoin={setInCoin}
                changeAmount={handle_in_amount}
                onRef={inRef}
              />
              <Divider>
                <div className="border-white border-opacity-25 border-2 bg-primary-200 rounded-2xl">
                  <img
                    src={SwapIcon}
                    alt=""
                    className="cursor-pointer"
                    onClick={onSwitchPairsClick}
                  />
                </div>
              </Divider>
              <SwapOutCard
                changeCoin={setOutCoin}
                changeAmount={handle_out_amount}
                outCoin={outCoin}
                onRef={outRef}
              />
            </div>
          </div>

          <PriceInfo
            isExactInAmount={isExactInAmount}
            tradeRoute={tradeRoute}
            slippageRate={slippageRate}
          />
        </Card>

        <div className="mt-1 p-1">
          {account ? (
            <Button
              disabled={swapBtnDisable}
              onClick={handle_swap}
              variant="contained"
              fullWidth
            >
              {swapBtnText}
            </Button>
          ) : (
            <LoginWallet local={1} />
          )}
        </div>
      </div>

      <div>
        <TransactionDialog
          openAlert={openAlert}
          txn={txn}
          title={`Swap  ${inAmount}  ${inSymbol} to ${outAmount}  ${outSymbol}`}
          step={step}
          handleCloseAlert={handleCloseAlert}
        />
      </div>
      <Impact
        onRef={impactRef}
        slippageRate={slippageRate}
        setSlippageRate={setSlippageRate}
      />
    </div>
  );
}
